import Honeybadger from "@honeybadger-io/js";
import { ClientId, FileIcon } from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import { MultipleFileUpload } from "components/shared/multiple-file-upload";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { SecondaryButton } from "components/ui/secondary-button";
import * as React from "react";
import { useCallback } from "react";
import { useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
interface ClientIntakeUploadDialogProps {
    clientId: ClientId;
    onDismiss: () => void;
    triggerClientLoad: (id: string) => void;
}

export const ClientIntakeUploadDialog: React.FC<ClientIntakeUploadDialogProps> = (
    props,
): JSX.Element => {
    const { onDismiss, clientId, triggerClientLoad } = props;
    const [hasUploadedFiles, setHasUploadedFiles] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const regionId = useRegionId();

    React.useEffect(() => {
        handleEvent("upload-client-form-start");
    }, []);

    const onAddNewFiles = useCallback(
        async (files: File[]) => {
            try {
                const formData = new FormData();
                files.forEach(file => formData.append("files", file));
                formData.append("regionId", regionId);

                const result = await fetch(`/api/clients/${clientId}/intake-file`, {
                    method: "POST",
                    body: formData,
                    credentials: "include",
                });

                if (!result.ok) {
                    const body = await result.json();
                    throw new Error(
                        `Failed to upload files. Please contact support, or try another file. Correlation ID: ${body.correlationId}`,
                    );
                }

                triggerClientLoad(clientId);
                setHasUploadedFiles(true);
                handleEvent("upload-client-form-upload");
            } catch (error) {
                Honeybadger.notify(error);
                setError(error.message);
            }
        },
        [clientId, regionId, triggerClientLoad],
    );

    return (
        <Panel
            type={PanelType.largeFixed}
            onDismiss={onDismiss}
            isOpen={true}
            headerText="Upload Client Intake Form">
            <div>
                <div className="mb-4 text-sm text-gray-600">
                    <p className="mb-2">
                        After printing and completing the client intake form, take a photo
                        of each page and upload them here. You can use your phone&apos;s
                        camera or select existing photos.
                    </p>
                    <p className="mb-2">
                        Make sure all text is clearly visible and the entire page is
                        included in each photo. Only JPEG photos are supported.
                    </p>
                    <p>
                        Having trouble?{" "}
                        <SupportEmailLink
                            label="Contact support"
                            subject="Client Intake Upload Help"
                        />{" "}
                        for assistance.
                    </p>
                </div>
                {error && <div className="text-red-500">{error}</div>}
                {!hasUploadedFiles ? (
                    <MultipleFileUpload
                        onAddNewFiles={onAddNewFiles}
                        title="Upload Client Intake PDF"
                        imageRenderInfo={{
                            fileGroupIcon: "02_IntakeComplete" as FileIcon,
                            fileGroupHeading: "Client Intake Files",
                        }}
                    />
                ) : (
                    <div className="flex-row-center">
                        <p className="mb-2">
                            The file has been uploaded successfully. Please review the
                            client data to ensure that it was imported correctly.
                        </p>
                        <SecondaryButton onClick={onDismiss}>Close</SecondaryButton>
                    </div>
                )}
            </div>
        </Panel>
    );
};
